import { Component, effect, inject, signal, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { DropDownButtonComponent, ButtonsModule } from '@progress/kendo-angular-buttons';
import { DateInputsModule } from '@progress/kendo-angular-dateinputs';
import { DropDownListModule } from '@progress/kendo-angular-dropdowns';
import { IconsModule, SVGIcon } from '@progress/kendo-angular-icons';
import { LabelModule } from '@progress/kendo-angular-label';
import { LayoutModule } from '@progress/kendo-angular-layout';
import { PDFExportComponent, PDFExportModule } from '@progress/kendo-angular-pdf-export';
import { TooltipsModule } from '@progress/kendo-angular-tooltip';
import { CyflarePeriodComponent } from '../../../../../core/components/cyflare-period/cyflare-period.component';
import { DispositionColumnComponent } from '../../../../charting/disposition-column/disposition-column.component';
import { SourceMapComponent } from './components/source-map/source-map.component';
import { SuspectUsersComponent } from './components/suspect-users/suspect-users.component';
import { TicketClosureBreakdownComponent } from './components/ticket-closure-breakdown/ticket-closure-breakdown.component';
import { TopDetectionsComponent } from './components/top-detections/top-detections.component';
import { AdrManualComponent } from './components/adr-manual/adr-manual.component';
import { DestinationMapComponent } from './components/destination-map/destination-map.component';
import { MttdComponent } from './components/mttd/mttd.component';
import { MttrComponent } from './components/mttr/mttr.component';
import { OutcomeStatusComponent } from './components/outcome-status/outcome-status.component';
import { SlaAdherenceComponent } from './components/sla-adherence/sla-adherence.component';
import { SuspectAssetsComponent } from './components/suspect-assets/suspect-assets.component';
import { BaseComponent } from '../../../../../core/shared/common/base.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SeriesClickEvent } from '@progress/kendo-angular-charts';
import { infoCircleIcon, moreVerticalIcon, sparklesIcon } from '@progress/kendo-svg-icons';
import { filter } from 'rxjs';
import { AnalyticsService, QueryType } from '../../../../../core/services/analytics.service';

@Component({
  encapsulation: ViewEncapsulation.None,
  selector: 'app-soc',
  standalone: true,
  imports: [
    DropDownListModule,
    DateInputsModule,
    DropDownButtonComponent,
    ButtonsModule,
    PDFExportModule,
    LabelModule,
    LayoutModule,
    SuspectUsersComponent,
    SuspectAssetsComponent,
    DestinationMapComponent,
    SourceMapComponent,
    OutcomeStatusComponent,
    RouterModule,
    TopDetectionsComponent,
    DispositionColumnComponent,
    AdrManualComponent,
    TicketClosureBreakdownComponent,
    MttrComponent,
    IconsModule,
    TooltipsModule,
    MttdComponent,
    SlaAdherenceComponent,
    CyflarePeriodComponent,
  ],
  templateUrl: './soc.component.html',
  styleUrl: './soc.component.scss',
  providers: [AnalyticsService],
})
export class SocComponent extends BaseComponent {
  @ViewChild('pdf', { static: true }) pdf!: PDFExportComponent;
  @ViewChild(SlaAdherenceComponent) slaAdherenceComponent!: SlaAdherenceComponent;
  @ViewChild(AdrManualComponent) adrManualComponent!: AdrManualComponent;
  @ViewChild(OutcomeStatusComponent) outcomeStatusComponent!: OutcomeStatusComponent;
  @ViewChild(MttrComponent) mttrComponent!: MttrComponent;
  @ViewChild(MttdComponent) mttdComponent!: MttdComponent;
  @ViewChild(TopDetectionsComponent) topDetectionsComponent!: TopDetectionsComponent;
  @ViewChild(SuspectUsersComponent) suspectedUsersComponent!: SuspectUsersComponent;
  @ViewChild(SuspectAssetsComponent) suspectAssetsComponent!: SuspectAssetsComponent;
  @ViewChild(DispositionColumnComponent) dispositionColumnComponent!: DispositionColumnComponent;
  @ViewChild(DestinationMapComponent) destinationMapComponent!: DestinationMapComponent;
  @ViewChild(SourceMapComponent) sourceMapComponent!: SourceMapComponent;
  @ViewChild(TicketClosureBreakdownComponent) ticketClosureBreakdownComponent!: TicketClosureBreakdownComponent;

  public infoCircleIcon: SVGIcon = infoCircleIcon;
  public moreVerticalIcon: SVGIcon = moreVerticalIcon;
  public sparklesIcon: SVGIcon = sparklesIcon;
  private route = inject(ActivatedRoute);
  private analyticsService = inject(AnalyticsService);

  public dateRange: string[] = [];
  public organizationId: number | null = null;

  public destinationMapData = signal<any>(null);
  public sourceMapData = signal<any>(null);
  public topSuspectUsersData = signal<any>(null);
  public topUserDetailData = signal<any>([]);
  public topSuspectAssetsData = signal<any>(null);
  public topAssetDetailData = signal<any>([]);
  public outcomeStatusData = signal<any>(null);
  public dispositionData = signal<any>(null);
  public topDetectionsData = signal<any>(null);
  public adrManualData = signal<any>(null);
  public mttrData = signal<any>(null);
  public ticketClosureData = signal<any>(null);
  public mttdData = signal<any>(null);
  public slaVoilationsData = signal<any>(null);
  public range = { start: null, end: null };
  public selectedDateRange = signal<string[]>([]);
  public outcomeData = signal<any>(null);
  public averageMttd = 0;
  public averageMttr = 0;

  constructor() {
    super();
    this.initEffect();
  }

  private initEffect() {
    effect(() => {
      this.route.queryParams
        .pipe(
          filter((params) => params['organizationId']),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe({
          next: (params) => {
            this.organizationId = +params['organizationId'];
            this.applyFilter();
          },
          error: (error) => {
            console.error('Error processing query parameters:', error);
          },
        });
    });
  }

  onSuspectUsersSeriesClick($event: SeriesClickEvent) {
    // Validate that we have data before processing click
    if (!this.topSuspectUsersData() || this.topSuspectUsersData().length === 0) {
      return;
    }

    const dateRange = this.selectedDateRange();
    const [current_start, current_end] = dateRange;

    // Clear existing data to show loading state
    this.topUserDetailData.set([]);

    this.analyticsService
      .getTopUserDetail(this.organizationId!, current_start, current_end, undefined, undefined, $event.category)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.topUserDetailData.set(response);
        },
        error: (error) => {
          console.error('Error fetching Top User Detail:', error);
        },
      });
  }

  onCaseEscalatedCheckboxChanged($event: boolean) {
    const dateRange = this.selectedDateRange();
    const params = {
      alert_received_on__range: dateRange.join(','),
      is_escalated: $event ? $event : null,
    };

    this.fetchMttdData(this.organizationId!, params);
  }

  onTopDetecttionsCaseEscalatedCheckboxChanged($event: boolean) {
    const dateRange = this.selectedDateRange();
    const params = {
      alert_received_on__range: dateRange.join(','),
      is_escalated: $event ? $event : null,
    };

    this.fetchTopDetectionsData(this.organizationId!, params);
  }

  onSuspectAssetsSeriesClick($event: SeriesClickEvent) {
    if (!this.topSuspectAssetsData() || this.topSuspectAssetsData().length === 0) {
      return;
    }

    const source_ip = $event.category;
    const [currentStart, currentEnd] = this.selectedDateRange();

    // Clear existing data to show loading state
    this.topAssetDetailData.set([]);

    this.analyticsService
      .getTopAssetDetail(this.organizationId!, currentStart, currentEnd, source_ip)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.topAssetDetailData.set(response);
        },
        error: (error) => {
          console.error('Error fetching Top User Detail:', error);
        },
      });
  }

  onContextMenuSelect(event: any) {
    let componentRef: any = null;
    // Determine the component reference based on the event name
    switch (event.name) {
      case 'escalationrelevancy':
        componentRef = this.outcomeStatusComponent;
        break;
      case 'adrmanual':
        componentRef = this.adrManualComponent;
        break;
      case 'sla':
        componentRef = this.slaAdherenceComponent;
        break;
      case 'mttr':
        componentRef = this.mttrComponent;
        break;
      case 'mttd':
        componentRef = this.mttdComponent;
        break;
      case 'topdetections':
        componentRef = this.topDetectionsComponent;
        break;
      case 'suspectedusers':
        componentRef = this.suspectedUsersComponent;
        break;
      case 'suspectedassets':
        componentRef = this.suspectAssetsComponent;
        break;
      case 'detectiondisposition':
        componentRef = this.dispositionColumnComponent;
        break;
      case 'destinationgeolocation':
        componentRef = this.destinationMapComponent;
        break;
      case 'sourcegeolocation':
        componentRef = this.sourceMapComponent;
        break;
      case 'ticketclosurebreakdown':
        componentRef = this.ticketClosureBreakdownComponent;
        break;
      default:
        console.warn(`No matching component for event name: ${event.name}`);
        break;
    }

    // If a valid componentRef is set, call the common method
    if (componentRef && typeof componentRef.onContextMenuSelect === 'function') {
      componentRef.onContextMenuSelect(event);
    } else {
      console.warn(
        `Component for event name "${event.name}" is not available or does not implement onContextMenuSelect.`
      );
    }
  }

  applyFilter() {
    this.dateRange = this.selectedDateRange();
    if (!this.dateRange) return;

    const params = {
      alert_received_on__range: this.dateRange.join(','),
    };
    const ticketDataParams = {
      created_time__range: this.dateRange.join(','),
    };
    const ticketDataParamsClosedTime = {
      ticket_closed_time__range: this.dateRange.join(','),
    }

    const [current_start, current_end] = this.dateRange;

    const mttdParams = {
      alert_received_on__range: this.dateRange.join(','),
      is_escalated: null,
    };

    if (this.organizationId !== null) {
      this.analyticsService
        .getDestinationMap(this.organizationId, current_start, current_end, QueryType.Filtered)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.destinationMapData.set(response);
          },
          error: (error) => {
            console.error('Error fetching Destination Map:', error);
          },
        });
      this.analyticsService
        .getSourceMap(this.organizationId, current_start, current_end, QueryType.Filtered)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.sourceMapData.set(response);
          },
          error: (error) => {
            console.error('Error fetching Source Map:', error);
          },
        });
      this.analyticsService
        .getTopSuspectUsers(this.organizationId, current_start, current_end, QueryType.Filtered)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.topSuspectUsersData.set(response);
          },
          error: (error) => {
            console.error('Error fetching Top Suspect Users:', error);
          },
        });
      this.fetchTopDetectionsData(this.organizationId, params);
      // topUserDetail is only loaded on drill-down click, not on initial load
      this.analyticsService
        .getTopSuspectAssets(this.organizationId, current_start, current_end, QueryType.Filtered)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.topSuspectAssetsData.set(response);
          },
          error: (error) => {
            console.error('Error fetching Top Suspect Assets:', error);
          },
        });
      // topAssetDetail is only loaded on drill-down click, not on initial load
      this.analyticsService
        .getOutcomeStatus(this.organizationId, ticketDataParams)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.outcomeStatusData.set(response);
          },
          error: (error) => {
            console.error('Error fetching Outcome Status:', error);
          },
        });
      this.analyticsService
        .getDisposition(this.organizationId, params)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.dispositionData.set(response);
          },
          error: (error) => {
            console.error('Error fetching Outcome Status:', error);
          },
        });
      // Convert date range to ISO format for adrmanual API
      const [startDate, endDate] = this.dateRange;
      this.analyticsService
        .getAdrManualData(this.organizationId, startDate, endDate, 'filtered', null)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.adrManualData.set(response);
          },
          error: (error) => {
            console.error('Error fetching ADR vs Manual Data:', error);
          },
        });
      this.analyticsService
        .getMttd(this.organizationId, params)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.averageMttd = response.value;
          },
          error: (error) => {
            console.error('Error fetching Average MTTD :', error);
          },
        });
      this.analyticsService
        .getMttrAverage(this.organizationId, ticketDataParamsClosedTime)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.averageMttr = response.value;
          },
          error: (error) => {
            console.error('Error fetching Average MTTR :', error);
          },
        });
      this.analyticsService
        .getMttrData(this.organizationId, ticketDataParamsClosedTime)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.mttrData.set(response);
          },
          error: (error) => {
            console.error('Error fetching MTTR Data:', error);
          },
        });
      this.analyticsService
        .getTicketClosureBreakdownData(this.organizationId, params)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.ticketClosureData.set(response);
          },
          error: (error) => {
            console.error('Error fetching ADR vs Manual Data:', error);
          },
        });
      this.analyticsService
        .getSlaAdherence(this.organizationId, params)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({
          next: (response) => {
            this.slaVoilationsData.set(response);
          },
          error: (error) => {
            console.error('Error fetching SLA voilations Data:', error);
          },
        });
      this.fetchMttdData(this.organizationId, mttdParams);
    } else {
      console.error('Organization ID is not set.');
    }
  }

  fetchMttdData(organizationId: number, params: any) {
    this.analyticsService
      .getMttdData(organizationId, params)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.mttdData.set(response);
        },
        error: (error) => {
          console.error('Error fetching MTTD Data:', error);
        },
      });
  }

  fetchTopDetectionsData(organizationId: number, params: any) {
    const [startDate, endDate] = this.dateRange;
    this.analyticsService
      .getTopDetectionData(
        organizationId,
        startDate,
        endDate,
        null, // security_tool not used in SOC v1
        params.is_escalated || null
      )
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (response) => {
          this.topDetectionsData.set(response);
        },
        error: (error) => {
          console.error('Error fetching Top Detections Data:', error);
        },
      });
  }

  showAISummary(chartType: string) {
    // You'll need to implement the logic for showing AI summary based on the chart type
    // This is just a basic example - modify according to your needs
    switch (chartType) {
      case 'adrmanual':
        const adrData = this.adrManualData();
        // Handle ADR Manual data summary
        console.log('Showing AI Summary for ADR Manual:', adrData);
        break;
      // Add more cases as needed for other chart types
      default:
        console.warn(`No AI summary handler for chart type: ${chartType}`);
    }
  }
}
